import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { Lock, LogOut, Building2 } from "lucide-react";

export function ProtectedRoute({ children, adminOnly = false, perm = null, superOnly = false }) {
  const { user, checking, isAdmin, isSuperAdmin, accessMode, accessMessage, accessPortal, can, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const signInAsAdmin = async () => {
    await logout();
    nav("/login", { replace: true });
  };
  if (checking)
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 animate-pulse">
          Authenticating…
        </div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <ForcePasswordChange />;
  if (superOnly && !isSuperAdmin) return <Navigate to="/" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  if (perm && !can(perm)) return <Navigate to="/" replace />;
  if (accessMode === "lockdown" && !isSuperAdmin)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-zinc-950 px-6 text-center" data-testid="lockdown-overlay">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10 text-red-400 ring-1 ring-red-500/30">
          <Lock size={28} />
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-white">{t("lockdown.title")}</h1>
        <p className="max-w-md text-sm text-zinc-400">{accessMessage || t("lockdown.desc")}</p>
        {accessPortal && (
          <div className="mt-1 max-w-md rounded-sm border border-zinc-700 bg-zinc-900/70 px-4 py-3 text-sm text-zinc-300" data-testid="lockdown-portal-notice">
            <p className="flex items-center justify-center gap-2 font-semibold text-white">
              <Building2 size={15} className="text-red-400" />
              {accessPortal.partner
                ? t("lockdown.contactPartner").replace("{partner}", accessPortal.partner)
                : t("lockdown.contactProvider")}
            </p>
            {accessPortal.portalUrl && (
              <a href={accessPortal.portalUrl} target="_blank" rel="noopener noreferrer"
                data-testid="lockdown-portal-link"
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-red-300 underline underline-offset-2 hover:text-red-200">
                {t("lockdown.openPortal")}
              </a>
            )}
          </div>
        )}
        <button onClick={signInAsAdmin} data-testid="lockdown-signin-admin"
          className="mt-2 inline-flex items-center gap-2 rounded-sm border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:border-zinc-500 hover:text-white">
          <LogOut size={15} /> {t("lockdown.signInAdmin")}
        </button>
      </div>
    );
  return children;
}
